/**
 * <PageHeader> — page-level title + breadcrumbs + actions slot.
 *
 * UI-SPEC §Typography: Display role (24px / 600) is reserved for page headers
 * and empty-state headlines — this component is THE primary consumer. Sits at
 * the top of every feature screen in HR Admin / Employee Web / Link Admin.
 *
 * Inside an AppShell the title goes up into the top bar (see
 * `PageTitle/PageTitle.tsx`) and the page keeps only the subtitle + actions;
 * with nothing left it renders nothing. `inBar={false}` keeps the heading in
 * the page as an `<h2>` (the bar's h1 names the page). Outside a shell it
 * renders exactly as before.
 *
 * Slots:
 *   - `breadcrumbs` — rendered above the title inside an accessible
 *     `<nav aria-label="Breadcrumb">` wrapper. Pair with `<Breadcrumbs>`.
 *   - `actions` — rendered at the logical-end edge of the title row. Expect
 *     a `<Button>` or a row of buttons.
 */
import * as React from "react";
import { cn } from "../../utils";
import { usePageTitle } from "../PageTitle/PageTitle";

export interface PageHeaderProps {
  /** Page title text. Rendered with the `text-display` typography role. */
  title: React.ReactNode;
  /** Plain text of `title` when it is a node — the bar tooltip + document title. */
  titleText?: string;
  /** Rendered beside the title, outside the heading (e.g. prev/next buttons). */
  titleAdornment?: React.ReactNode;
  /** Default true: inside an AppShell the title moves into the top bar. */
  inBar?: boolean;
  /**
   * Optional muted line under the title (reference design's `<PageHead sub=…>`).
   * Typical use: row count, last-updated stamp, contextual hint. Renders only
   * when present.
   */
  subtitle?: React.ReactNode;
  /** Optional breadcrumb trail — typically a `<Breadcrumbs>` element. */
  breadcrumbs?: React.ReactNode;
  /**
   * Optional actions slot — rendered at the logical-end edge of the title row.
   * Typically one or more `<Button>` elements.
   */
  actions?: React.ReactNode;
  /** Consumer className forwarded to the outer `<header>`. */
  className?: string;
}

const present = (n: React.ReactNode): boolean => n !== undefined && n !== null && n !== false;

export function PageHeader({
  title,
  titleText,
  titleAdornment,
  inBar = true,
  subtitle,
  breadcrumbs,
  actions,
  className,
}: PageHeaderProps) {
  const bar = usePageTitle({ title, titleText, inBar, titleAdornment });
  const titleInBar = bar.inShell && inBar;

  if (titleInBar && !present(breadcrumbs) && !present(subtitle) && !present(actions)) {
    return <>{bar.titleNode}</>;
  }

  const Heading = bar.inShell ? "h2" : "h1";
  const heading = titleInBar ? null : titleAdornment !== undefined ? (
    <div className="flex min-w-0 items-center gap-xs">
      <Heading className="text-page-title">{title}</Heading>
      {titleAdornment}
    </div>
  ) : (
    <Heading className="text-page-title">{title}</Heading>
  );

  return (
    <>
      {bar.titleNode}
      <header className={cn("flex flex-col gap-sm pb-lg border-b border-subtle", className)}>
        {breadcrumbs !== undefined && <nav aria-label="Breadcrumb">{breadcrumbs}</nav>}
        {/* flex-wrap: on narrow phone viewports the title + whitespace-nowrap
            action buttons can't share one line; without wrap the actions row
            forces page-level horizontal scroll (unshrinkable min-content). On
            wide screens nothing changes — the row fits and never wraps. */}
        <div className="flex flex-wrap items-center justify-between gap-md">
          <div className="flex min-w-0 flex-col gap-xs">
            {heading}
            {subtitle !== undefined && <p className="text-body text-muted">{subtitle}</p>}
          </div>
          {actions !== undefined && <div className="flex flex-wrap gap-sm">{actions}</div>}
        </div>
      </header>
    </>
  );
}
